import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import quizStore from '../store/QuizStore';

import PaginationComponent from 'src/components/PaginationComponent';
import { QuizObj } from 'src/types/quiz';
import QuestionTable from '../components/QuestionTable';
import QuizDetailsEditor from '../components/QuizDetailsEditor';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { Button } from '../components/ui/button';
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { useToast } from '../components/ui/use-toast';

const QuizPage: React.FC<{ quizzes: QuizObj[] }> = ({ quizzes }) => {
  const [createQuizName, setQuizName] = useState<string>('');
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [localQuizzes, setLocalQuizzes] = useState<QuizObj[]>(quizzes);
  const [draggedQuiz, setDraggedQuiz] = useState<QuizObj | null>(null);

  const itemsPerPage = 50;
  const { toast } = useToast();

  useEffect(() => {
    const sortedQuizzes = quizzes.slice().sort((a, b) => {
      if (a.lastUpdated > b.lastUpdated) return -1;
      if (a.lastUpdated < b.lastUpdated) return 1;
      return 0;
    });
    console.log({
      quizzes: sortedQuizzes.map((q) => [q.name, q.lastUpdated]),
    });
    setLocalQuizzes(sortedQuizzes);
  }, [quizzes]);

  const paginatedQuizzes = localQuizzes.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  const handleQuizDetailsUpdate = ({
    title,
    description,
  }: {
    title: string;
    description: string;
  }) => {
    if (!quizStore.currentQuizId || !title) return;

    quizStore.renameQuiz(quizStore.currentQuizId, title);
    quizStore.updateQuizDescription(quizStore.currentQuizId, description);

    toast({
      title: 'Quiz Updated',
      description: 'The quiz details have been saved successfully.',
    });
  };

  const handleDeleteQuiz = () => {
    if (!quizStore.currentQuizId) {
      throw new Error('No quiz selected');
    }
    const quizName = quizStore.currentQuizName;
    // Delete quiz changes the current quiz to the next one
    quizStore.deleteQuiz(quizStore.currentQuizId);
    setLocalQuizzes(
      localQuizzes.filter((q) => q.id !== quizStore.currentQuizId),
    );
    toast({
      title: 'Quiz Deleted',
      description: `The quiz "${quizName}" has been deleted.`,
    });
  };

  const handleDragStart = (
    e: React.DragEvent<HTMLDivElement>,
    quiz: QuizObj,
  ) => {
    setDraggedQuiz(quiz);
    e.dataTransfer.setData('text/plain', quiz.name);
    e.currentTarget.style.opacity = '0.5';
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.currentTarget.style.background = '#f0f0f0';
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.currentTarget.style.background = '';
  };

  const handleDrop = (
    e: React.DragEvent<HTMLDivElement>,
    targetQuiz: QuizObj,
  ) => {
    e.preventDefault();
    e.currentTarget.style.background = '';

    if (draggedQuiz) {
      const targetIndex = localQuizzes.findIndex(
        (q) => q.name === targetQuiz.name,
      );
      const updatedQuizzes = localQuizzes.filter(
        (q) => q.name !== draggedQuiz.name,
      );
      updatedQuizzes.splice(targetIndex, 0, draggedQuiz);

      setLocalQuizzes(updatedQuizzes);
      quizStore.reorderQuiz(draggedQuiz.name, targetIndex);
    }

    setDraggedQuiz(null);
  };

  const handleDragEnd = (e: React.DragEvent<HTMLDivElement>) => {
    e.currentTarget.style.opacity = '1';
  };

  const toggleFavourite = (quizId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    quizStore.toggleFavourite(quizId);
  };

  return (
    <div className="flex flex-row space-x-4 p-4">
      <div className="w-1/2 min-w-fit">
        <Card className="p-6">
          <CardHeader>
            <h3 className="text-2xl font-semibold">Create Quiz</h3>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="quiz-name">Quiz Name</Label>
              <Input
                id="quiz-name"
                type="text"
                value={createQuizName}
                onChange={(e) => setQuizName(e.target.value)}
                placeholder="Enter quiz name"
              />
            </div>
            <Button
              onClick={async () => {
                if (createQuizName === '') return;
                const newQuiz = await quizStore.createNewQuiz(createQuizName);
                if (!newQuiz) return;
                quizStore.setCurrentQuiz(newQuiz.id);
                setLocalQuizzes([newQuiz, ...localQuizzes]);
                setQuizName('');
              }}
              className="w-full"
              disabled={createQuizName === ''}
            >
              Create New Quiz
            </Button>
            <Separator />
            <div className="space-y-2">
              <Label htmlFor="file-upload">Import Quiz</Label>
              <Input
                id="file-upload"
                type="file"
                accept=".tsv"
                onChange={(e) => quizStore.importFromTSV(e.target.files?.[0])}
              />
            </div>
          </CardContent>
          <CardFooter className="text-sm text-gray-500">
            You can create a new quiz or import from a TSV file.
          </CardFooter>
          <Separator className="mb-8" />
          <h3 className="text-2xl font-semibold mb-4">Select Quiz</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {paginatedQuizzes.map((quiz) => (
              <Card
                key={quiz.name}
                className={`cursor-pointer transition-all duration-200 hover:shadow-md ${
                  quiz.id === quizStore.currentQuizId
                    ? 'ring-2 ring-primary'
                    : ''
                }`}
                onClick={() => quizStore.setCurrentQuiz(quiz.id)}
                // uncomment this line to make these sortable
                // this is commented out because it doesnt persist in firestore
                // draggable
                onDragStart={(e) => handleDragStart(e, quiz)}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, quiz)}
                onDragEnd={handleDragEnd}
              >
                <CardHeader className="pb-0">
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-semibold truncate">
                      {quiz.name}
                    </h3>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="ml-2 flex-shrink-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleFavourite(quiz.id, e);
                      }}
                    >
                      {quiz.favourite ? '★' : '☆'}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="pb-6">
                  <p className="text-sm text-muted-foreground">
                    {
                      quizStore.questions.filter((q) => q.quizId === quiz.id)
                        .length
                    }{' '}
                    questions
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
          {paginatedQuizzes.length !== localQuizzes.length &&
            localQuizzes.length > itemsPerPage && (
              <div className="flex items-center justify-between space-x-2 py-4">
                <PaginationComponent
                  currentPage={currentPage}
                  totalPages={Math.ceil(localQuizzes.length / itemsPerPage)}
                  setCurrentPage={setCurrentPage}
                />
              </div>
            )}
        </Card>
      </div>
      <div className="w-1/2 min-w-fit">
        <Card className="p-6">
          {quizStore.currentQuizId && (
            <QuizDetailsEditor
              title={quizStore.currentQuizName || ''}
              description={
                quizStore.quizzes.find((q) => q.id === quizStore.currentQuizId)
                  ?.description || ''
              }
              onSave={handleQuizDetailsUpdate}
              className="mb-4"
            />
          )}
          <div className="flex mb-4">
            <Button
              onClick={() => quizStore.exportToTSV(quizStore.currentQuizId)}
              className="mr-2"
            >
              Export to TSV
            </Button>
            <Button
              onClick={() =>
                quizStore.exportToCanvaCSV(quizStore.currentQuizId)
              }
              className="mr-2"
            >
              Export for Canva
            </Button>
            <AlertDialog
              open={isDeleteDialogOpen}
              onOpenChange={setIsDeleteDialogOpen}
            >
              <AlertDialogTrigger asChild>
                <Button variant="destructive">Delete</Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>
                    Are you sure you want to delete this quiz?
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    This action cannot be undone. This will permanently delete
                    the "{quizStore.currentQuizName}" quiz and all its
                    questions.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleDeleteQuiz}>
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
          <QuestionTable
            questions={quizStore.currentQuizQuestions}
            isSingleQuiz={true}
          />
        </Card>
      </div>
    </div>
  );
};

export default observer(QuizPage);
